import { useState } from "react";
import { toast } from "sonner";
import { BookOpen, Gavel, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errText } from "@/lib/api";
import { typeStyles } from "@/lib/eventMeta";
import { BriefItemEditor } from "@/components/BriefItemEditor";
import { Markdown } from "@/components/Markdown";

const ItemBody = ({ item }) => (
  <>
    <Markdown text={item.instruction} className="text-base leading-relaxed" />
    <div className="mt-2 flex flex-wrap gap-2">
      {item.bare_acts ? (
        <span className="chip chip-session chip-wrap inline-flex gap-1.5">
          <BookOpen className="h-3.5 w-3.5 shrink-0 mt-[0.15rem]" /> {item.bare_acts}
        </span>
      ) : null}
      {item.judgments ? (
        <span className="chip chip-post chip-wrap inline-flex gap-1.5">
          <Gavel className="h-3.5 w-3.5 shrink-0 mt-[0.15rem]" /> {item.judgments}
        </span>
      ) : null}
      {item.source === "voice" ? <span className="chip chip-oos">from voice note</span> : null}
    </div>
  </>
);

export const BriefListing = ({ listing, date, canWrite, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const bar = typeStyles[listing.event_type]?.bar || "bar-session";

  const save = async (form, item) => {
    if (!form.instruction.trim()) return toast.error("Write the instruction first");
    const payload = {
      date,
      matter_id: item?.matter_id ?? listing.matter_id ?? null,
      event_id: item?.event_id ?? listing.event_id ?? null,
      instruction: form.instruction.trim(),
      bare_acts: form.bare_acts?.trim() || null,
      judgments: form.judgments?.trim() || null,
      is_general: !!item?.is_general,
    };
    try {
      if (item?.item_id) await api.put(`/brief/items/${item.item_id}`, payload);
      else await api.post("/brief/items", payload);
      setAdding(false);
      setEditing(null);
      onChange();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/brief/items/${item.item_id}`);
      toast.success("Instruction removed");
      onChange();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const meta = [listing.case_number, listing.court_name, listing.location].filter(Boolean).join(" · ");

  return (
    <article className="surface p-5 flex gap-4" data-testid={`brief-listing-${listing.event_id}`}>
      <span className={`w-1.5 self-stretch rounded-sm shrink-0 ${bar}`} />
      <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="label-caps">{listing.when}</p>
          <h3 className="text-base md:text-lg font-heading font-semibold mt-1">{listing.title}</h3>
          {meta ? <p className="text-sm text-muted-foreground mt-1">{meta}</p> : null}
          {listing.listing_details ? (
            <p className="text-sm mt-1.5">
              <span className="font-semibold">Listing: </span>
              <Markdown
                text={listing.listing_details}
                inline
                testId={`brief-listing-details-${listing.event_id}`}
              />
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {listing.urgent ? <span className="chip chip-fixed">Urgent</span> : null}
          {canWrite && !adding ? (
            <button
              data-testid={`brief-add-item-${listing.event_id}`}
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-sm border border-border hover:bg-accent transition-colors duration-200"
            >
              <Plus className="h-4 w-4" /> Instruction
            </button>
          ) : null}
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {listing.items.map((item) =>
          editing === item.item_id ? (
            <BriefItemEditor
              key={item.item_id}
              item={item}
              testId={`brief-edit-${item.item_id}`}
              onSave={(form) => save(form, item)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div
              key={item.item_id}
              data-testid={`brief-item-${item.item_id}`}
              className="border border-border rounded-sm px-4 py-3 flex items-start gap-3"
            >
              <div className="min-w-0 flex-1">
                <ItemBody item={item} />
              </div>
              {canWrite ? (
                <div className="flex shrink-0 gap-1">
                  <button
                    data-testid={`brief-edit-btn-${item.item_id}`}
                    onClick={() => setEditing(item.item_id)}
                    className="h-9 w-9 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    data-testid={`brief-delete-btn-${item.item_id}`}
                    onClick={() => remove(item)}
                    className="h-9 w-9 grid place-items-center rounded-sm text-destructive hover:bg-destructive/10 transition-colors duration-200"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ) : null}
            </div>
          ),
        )}

        {adding ? (
          <BriefItemEditor
            testId={`brief-new-${listing.event_id}`}
            onSave={(form) => save(form, null)}
            onCancel={() => setAdding(false)}
          />
        ) : null}

        {!listing.items.length && !adding ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm px-4 py-3">
            No instruction yet — attend and report.
          </p>
        ) : null}
      </div>
      </div>
    </article>
  );
};
